import * as React from 'react';
import { ListChevronsDownUpIcon, ListChevronsUpDownIcon } from 'lucide-react';
import { useMutation } from 'react-query';
import { useTranslation } from 'hooks';
import { useFetchTollPreview } from 'hooks/api/tolls';
import type {
  TripDirection,
  FetchTollPreviewDataPayload,
  TollPreviewDataResponseData,
} from 'contracts/transport-routes';
import { Card, CardContent, CardHeader } from 'components/ui/card';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from 'components/ui/collapsible';
import { useForwardTripState, useBackwardTripState } from 'pages/Settings/transport-routes/hooks';

import { HeaderContent } from './header-content';
import { TollsList } from './tolls-list';

type Props = React.HTMLAttributes<HTMLDivElement> & {
  tripDirection: TripDirection;
}

export const TollsPreview = ({ tripDirection, className, ...props }: Props) => {
  const { fetchTollPreview } = useFetchTollPreview();

  const { forwardTripFormValues, forwardTripStopLocations } = useForwardTripState();
  const { backwardTripFormValues, backwardTripStopLocations } = useBackwardTripState();

  const [open, setOpen] = React.useState(false);
  const [tollPreviewData, setTollPreviewData] = React.useState<TollPreviewDataResponseData>();

  const queryPayload = React.useMemo((): FetchTollPreviewDataPayload | null => {
    const {
      origin_uf,
      origin_ibge,
      destination_uf,
      destination_ibge,
    } = tripDirection === 'forward'
      ? forwardTripFormValues
      : backwardTripFormValues;

    const originAndDestinationUndefined = [
      origin_uf,
      origin_ibge,
      destination_uf,
      destination_ibge,
    ].some(value => value === undefined);

    if (originAndDestinationUndefined) {
      return null;
    }

    return {
      origin: { uf: origin_uf!, ibge: origin_ibge! },
      destination: { uf: destination_uf!, ibge: destination_ibge! },
      stops: tripDirection === 'forward'
        ? forwardTripStopLocations
        : backwardTripStopLocations,
    };

  }, [
    tripDirection,
    forwardTripFormValues,
    backwardTripFormValues,
    forwardTripStopLocations,
    backwardTripStopLocations,
  ]);

  const tollMutation = useMutation({
    mutationKey: ['fetch-toll-preview', queryPayload],
    mutationFn: fetchTollPreview,
    onSuccess: setTollPreviewData,
    onError: console.error,
  });

  const executeTollPreviewMutation = React.useCallback(() => {
    if (!queryPayload) return;
    tollMutation.mutate(queryPayload);
  }, [queryPayload]);

  const ToggleIcon = React.useMemo(() => {
    return open
      ? ListChevronsDownUpIcon
      : ListChevronsUpDownIcon;
  }, [open]);

  React.useEffect(() => {
    executeTollPreviewMutation();
  }, [executeTollPreviewMutation]);

  if (!queryPayload) {
    return null;
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card {...props} className={className}>
        <CardHeader className={'flex flex-row items-center'}>
          <HeaderContent
            tollPreviewData={tollPreviewData}
            loading={tollMutation.isLoading}
          />
          <CollapsibleTrigger className={'ml-10'}>
            <ToggleIcon />
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
          <CardContent>
            <TollsList
              tolls={tollPreviewData?.tolls}
              loading={tollMutation.isLoading}
            />
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};